import {
  JobsApi,
  BackupsApi,
  ListBackupJobsTypesApi,
  ListRestoreJobsTypesApi,
  ListScanJobsTypesApi,
  ListBackupJobErrorCodesApi,
  ListRestoreJobErrorCodesApi,
  ListScanJobErrorCodesApi,
  JobStatus,
  JobProgressStepStatus,
} from '@repo/api-gw-sdk';
import type {
  Condition,
  Configuration,
  GetJobProgressStepsResponse,
  JobProgressStep,
  VaultProtectionMode,
  VaultStorageTier,
} from '@repo/api-gw-sdk';
import type { ColumnSort } from '@tanstack/react-table';

import type { EntraRestoreReport } from './entraRestoreReportTypes';
import type { HttpClient } from './httpClient';

const baseUrl = '/jobs';

const JOB_DETAIL_REFRESH_INTERVAL_MS = 15_000;
const JOB_LIST_REFRESH_INTERVAL_MS = 30_000;
const JOB_STEPS_REFRESH_INTERVAL_MS = 10_000;

// A machine backup exports its database after the job is already reported completed, so the steps
// keep refreshing while any of them is still running even once the job itself is no longer active.
// Recording is best-effort: a row whose terminal write was lost stays running forever, so a step
// running for longer than any real post-completion phase is not worth polling for. The longest
// such phase is the database export, which the backup lets hold its per-instance lock for 7 days.
const STALE_RUNNING_STEP_MS = 7 * 24 * 60 * 60 * 1000;

const stepStillRunning = (step: JobProgressStep): boolean => {
  if (
    step.derivedStatus !==
    JobProgressStepStatus.JOB_PROGRESS_STEP_STATUS_RUNNING
  ) {
    return false;
  }
  const startedAt = step.derivedStartedAt ?? step.startedAt;
  return !startedAt || Date.now() - startedAt.getTime() < STALE_RUNNING_STEP_MS;
};

const stepsRefreshInterval =
  (isJobActive: boolean) =>
  (latest?: GetJobProgressStepsResponse): number =>
    isJobActive || latest?.steps?.some(stepStillRunning)
      ? JOB_STEPS_REFRESH_INTERVAL_MS
      : 0;

const ACTIVE_JOB_STATUSES = new Set([
  JobStatus.JOB_RUNNING,
  JobStatus.JOB_PENDING,
  JobStatus.JOB_UNSPECIFIED,
]);

const isActiveJobStatus = (status: JobStatus | undefined) =>
  !status || ACTIVE_JOB_STATUSES.has(status);

export const jobs = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const jobsApi = new JobsApi(config);
  const backupsApi = new BackupsApi(config);
  const listBackupJobTypesApi = new ListBackupJobsTypesApi(config);
  const listScanJobTypesApi = new ListScanJobsTypesApi(config);
  const listRestoreJobTypesApi = new ListRestoreJobsTypesApi(config);
  const listBackupJobErrorCodesApi = new ListBackupJobErrorCodesApi(config);
  const listRestoreJobErrorCodesApi = new ListRestoreJobErrorCodesApi(config);
  const listScanJobErrorCodesApi = new ListScanJobErrorCodesApi(config);
  return {
    downloadBackupJobs: (columnFilters?: Condition, columns?: string) =>
      jobsApi.backupJobsDownload(currentProjectId, undefined, columns, {
        filter: columnFilters,
      }),
    downloadRestoreJobs: (columnFilters?: Condition, columns?: string) =>
      jobsApi.restoreJobsDownload(currentProjectId, undefined, columns, {
        filter: columnFilters,
      }),
    downloadScanJobs: (columnFilters?: Condition, columns?: string) =>
      jobsApi.scanJobsDownload(currentProjectId, undefined, columns, {
        filter: columnFilters,
      }),
    scan: {
      useJob: (
        jobId: string,
        swrOptions?: {
          fallbackData?: Awaited<ReturnType<typeof jobsApi.getScanJob>>;
        }
      ) =>
        httpClient.executeWithConfig(
          jobsApi.getScanJob.bind(jobsApi),
          jobId ? baseUrl : null,
          '/scan',
          {
            refreshInterval: (data) =>
              isActiveJobStatus(data?.job?.jobExecutionDetails.status)
                ? JOB_DETAIL_REFRESH_INTERVAL_MS
                : 0,
            ...swrOptions,
          },
          currentProjectId,
          jobId
        ),
      useList: (
        pageIndex: number,
        pageSize: number,
        sorting: ColumnSort | undefined,
        columnFilters: Condition | undefined
      ) =>
        httpClient.executeWithConfig(
          jobsApi.listScanJobsInternal.bind(jobsApi),
          baseUrl,
          '/scan',
          { refreshInterval: JOB_LIST_REFRESH_INTERVAL_MS },
          currentProjectId,
          pageIndex,
          pageSize,
          sorting
            ? `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`
            : undefined,
          { filter: columnFilters }
        ),
      useListTypes: () =>
        httpClient.execute(
          listScanJobTypesApi.listScanJobsTypes.bind(listScanJobTypesApi),
          baseUrl,
          '/list-scan-job-types',
          currentProjectId
        ),
      useListErrorCodes: () =>
        httpClient.execute(
          listScanJobErrorCodesApi.listScanJobErrorCodes.bind(
            listScanJobErrorCodesApi
          ),
          baseUrl,
          '/list-scan-job-error-codes',
          currentProjectId
        ),
    },
    backup: {
      cancel: (jobId: string) => {
        return jobsApi.cancelBackupJob(currentProjectId, jobId);
      },
      create: (
        resourceId: string,
        vaultId: string,
        retentionDays: number,
        isPitrOnDemand = false,
        vaultStorageTier?: VaultStorageTier
      ) => {
        return backupsApi.takeSnapshot(currentProjectId, resourceId, {
          retentionDays,
          vaultId,
          isPitrOnDemand,
          vaultStorageTier,
        });
      },
      createNative: (
        resourceId: string,
        retentionDays: number,
        targetRegion?: string,
        vaultProtectionMode?: VaultProtectionMode
      ) => {
        return backupsApi.takeNativeSnapshot(currentProjectId, resourceId, {
          retentionDays,
          targetRegion,
          vaultProtectionMode,
        });
      },
      useJob: (
        jobId: string,
        swrOptions?: {
          fallbackData?: Awaited<ReturnType<typeof jobsApi.getBackupJob>>;
        }
      ) =>
        httpClient.executeWithConfig(
          jobsApi.getBackupJob.bind(jobsApi),
          jobId ? baseUrl : null,
          '/backup',
          {
            refreshInterval: (data) =>
              isActiveJobStatus(data?.job?.jobExecutionDetails.status)
                ? JOB_DETAIL_REFRESH_INTERVAL_MS
                : 0,
            ...swrOptions,
          },
          currentProjectId,
          jobId
        ),
      useList: (
        pageIndex: number,
        pageSize: number,
        sorting: ColumnSort | undefined,
        columnFilters: Condition | undefined
      ) =>
        httpClient.executeWithConfig(
          jobsApi.listBackupJobsInternal.bind(jobsApi),
          baseUrl,
          '/backup',
          { refreshInterval: JOB_LIST_REFRESH_INTERVAL_MS },
          currentProjectId,
          pageIndex,
          pageSize,
          sorting
            ? `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`
            : undefined,
          { filter: columnFilters }
        ),
      useListTypes: () =>
        httpClient.execute(
          listBackupJobTypesApi.listBackupJobsTypes.bind(listBackupJobTypesApi),
          baseUrl,
          '/list-backup-job-types',
          currentProjectId
        ),
      useListErrorCodes: () =>
        httpClient.execute(
          listBackupJobErrorCodesApi.listBackupJobErrorCodes.bind(
            listBackupJobErrorCodesApi
          ),
          baseUrl,
          '/list-backup-job-error-codes',
          currentProjectId
        ),
      useJobProgressSteps: (jobId: string, isJobActive: boolean) =>
        httpClient.executeWithConfig(
          jobsApi.getBackupJobProgressSteps.bind(jobsApi),
          jobId ? baseUrl : null,
          '/backup/progress-steps',
          { refreshInterval: stepsRefreshInterval(isJobActive) },
          currentProjectId,
          jobId
        ),
    },
    restore: {
      cancel: (jobId: string) => {
        return jobsApi.cancelRestoreJob(currentProjectId, jobId);
      },
      useJob: (
        jobId: string,
        swrOptions?: {
          fallbackData?: Awaited<ReturnType<typeof jobsApi.getRestoreJob>>;
        }
      ) =>
        httpClient.executeWithConfig(
          jobsApi.getRestoreJob.bind(jobsApi),
          jobId ? baseUrl : null,
          '/restore',
          {
            refreshInterval: (data) =>
              isActiveJobStatus(data?.job?.jobExecutionDetails.status)
                ? JOB_DETAIL_REFRESH_INTERVAL_MS
                : 0,
            ...swrOptions,
          },
          currentProjectId,
          jobId
        ),
      useList: (
        pageIndex: number,
        pageSize: number,
        sorting: ColumnSort | undefined,
        columnFilters: Condition | undefined,
        includeInternal: boolean
      ) =>
        httpClient.executeWithConfig(
          jobsApi.listRestoreJobsInternal.bind(jobsApi),
          baseUrl,
          '/restore',
          { refreshInterval: JOB_LIST_REFRESH_INTERVAL_MS },
          currentProjectId,
          pageIndex,
          pageSize,
          sorting
            ? `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`
            : undefined,
          includeInternal,
          { filter: columnFilters }
        ),
      useListTypes: () =>
        httpClient.execute(
          listRestoreJobTypesApi.listRestoreJobsTypes.bind(
            listRestoreJobTypesApi
          ),
          baseUrl,
          '/list-restore-job-types',
          currentProjectId
        ),
      useListErrorCodes: () =>
        httpClient.execute(
          listRestoreJobErrorCodesApi.listRestoreJobErrorCodes.bind(
            listRestoreJobErrorCodesApi
          ),
          baseUrl,
          '/list-restore-job-error-codes',
          currentProjectId
        ),
      useJobProgressSteps: (jobId: string, isJobActive: boolean) =>
        httpClient.executeWithConfig(
          jobsApi.getRestoreJobProgressSteps.bind(jobsApi),
          jobId ? baseUrl : null,
          '/restore/progress-steps',
          { refreshInterval: stepsRefreshInterval(isJobActive) },
          currentProjectId,
          jobId
        ),
      // No generated SDK method for this endpoint yet, so it goes through the
      // raw httpClient against a hand-built URL. The shared fetcher resolves
      // non-2xx responses to their JSON error body rather than rejecting, so a
      // 404 surfaces as a truthy error object — callers must validate the
      // payload shape (reportType) rather than rely on absence.
      fetchRestoreReportHtml: (jobId: string) =>
        httpClient.fetchText(
          `/projects/${currentProjectId}/restore-jobs/${jobId}/restore-report/html`
        ),
      useRestoreReport: (jobId: string) =>
        httpClient.get(
          jobId
            ? `/projects/${currentProjectId}/restore-jobs/${jobId}/restore-report`
            : null,
          { shouldRetryOnError: false }
        ) as {
          body: EntraRestoreReport | undefined;
          isLoading: boolean;
          error: unknown;
        },
    },
  };
};
